"use client";
import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { getMyTenant } from "@/services/api";
import { getUser } from "@/services/auth";

interface TenantSettings {
    _id: string;
    address: string;
    contactNumbers: string[];
    currency: string;
    isActive: boolean;
    logoUrl: string;
    tenantId: string;
    timezone: string;
    updatedAt: string;
    updatedBy: string;
    defaultTheme?: 'light' | 'dark';
    language?: 'english' | 'hindi' | 'marathi';
    receiptTerms?: string[];
    billTerms?: string[];
    isOwnerFieldsEnabled?: boolean;
    isDriverFieldsEnabled?: boolean;
}

export type BusinessType = 'PARKING' | 'RAILWAY_CLOAK_ROOM' | 'ROAD_TAX';

interface Tenant {
    _id: string;
    name: string;
    code: string;
    plan: string;
    isActive: boolean;
    totalAvailableSpace?: number;
    businessType: BusinessType;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

interface ActiveLegalDoc {
    _id: string;
    type: string;
    title: string;
    content: string;
    version: number;
}

interface TenantData {
    tenant: Tenant;
    settings: TenantSettings;
    features: Record<string, boolean | number>;
    activeLegalDocs?: Record<string, ActiveLegalDoc>;
}

interface TenantContextType {
    tenantData: TenantData | null;
    isSuperAdmin: boolean;
    loading: boolean;
    error: string | null;
    activeLegalDocs: Record<string, ActiveLegalDoc>;
    refetchTenantData: () => Promise<void>;
    clearTenantData: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
    const [tenantData, setTenantData] = useState<TenantData | null>(null);
    // isSuperAdmin comes from the server response — NOT from localStorage.
    // This means even if someone edits localStorage to fake SYSTEM_OWNER,
    // the frontend won't grant bypass (and the backend will still reject API calls via JWT).
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeLegalDocs, setActiveLegalDocs] = useState<Record<string, ActiveLegalDoc>>({});

    const fetchTenantData = useCallback(async () => {
        setLoading(true);
        try {
            const user = getUser();
            if (!user) {
                setTenantData(null);
                setLoading(false);
                return;
            }
            const cached = sessionStorage.getItem('tenant_data');
            if (cached) {
                const parsed = JSON.parse(cached);
                setTenantData(parsed.tenantData);
                setIsSuperAdmin(parsed.isSuperAdmin);
                setActiveLegalDocs(parsed.tenantData?.activeLegalDocs || {});
                setLoading(false);
                return;
            }
            const data = await getMyTenant();
            if (data.isSuperAdmin === true) {
                setIsSuperAdmin(true);
                setTenantData(null);
            } else {
                setIsSuperAdmin(false);
                setTenantData(data);
                setActiveLegalDocs(data.activeLegalDocs || {});
                sessionStorage.setItem('tenant_data', JSON.stringify({
                    tenantData: data,
                    isSuperAdmin: false
                }));
            }
            setError(null);
        } catch (err) {
            setError("Failed to load tenant data");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchTenantData();
    }, [fetchTenantData]);

    const clearTenantData = useCallback(() => {
        sessionStorage.removeItem('tenant_data');
        setTenantData(null);
        setIsSuperAdmin(false);
        setError(null);
        setActiveLegalDocs({});
    }, []);

    const value = useMemo<TenantContextType>(
        () => ({
            tenantData,
            isSuperAdmin,
            loading,
            error,
            activeLegalDocs,
            refetchTenantData: fetchTenantData,
            clearTenantData,
        }),
        [tenantData, isSuperAdmin, loading, error, activeLegalDocs, fetchTenantData, clearTenantData]
    );

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
}

export function useTenant(): TenantContextType {
    const context = useContext(TenantContext);
    if (context === undefined) {
        throw new Error("useTenant must be used within a TenantProvider");
    }
    return context;
}
